import { createPortal } from "react-dom";
import { useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { MdKeyboardArrowDown } from "react-icons/md";
import { brandSelectClass } from "./fieldStyles";
import { FieldShell } from "./FieldShell";

function toOptionValue(value) {
  return value == null ? "" : String(value);
}

export function BrandSelect({
  label,
  name,
  value,
  onChange,
  onBlur,
  options,
  placeholder = "Seleccionar",
  helper,
  error,
  disabled = false,
  required = false,
  className = "",
  showLabel = true,
  disableHoverEffects = false,
}) {
  const rootRef = useRef(null);
  const menuRef = useRef(null);
  const listboxId = useId();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);

  const selectedOption = useMemo(() => {
    const currentValue = toOptionValue(value);
    return options.find((option) => toOptionValue(option.value) === currentValue) ?? null;
  }, [options, value]);

  useEffect(() => {
    function handlePointerDown(event) {
      if (!rootRef.current?.contains(event.target) && !menuRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    }

    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setIsOpen(false);
      }
    }

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || disabled) {
      setMenuPosition(null);
      return undefined;
    }

    function updateMenuPosition() {
      const buttonElement = rootRef.current?.querySelector("button");

      if (!buttonElement) {
        return;
      }

      const rect = buttonElement.getBoundingClientRect();

      setMenuPosition({
        width: rect.width,
        left: rect.left,
        top: rect.bottom + 8,
      });
    }

    updateMenuPosition();

    window.addEventListener("resize", updateMenuPosition);
    window.addEventListener("scroll", updateMenuPosition, true);

    return () => {
      window.removeEventListener("resize", updateMenuPosition);
      window.removeEventListener("scroll", updateMenuPosition, true);
    };
  }, [disabled, isOpen, options.length]);

  const commitValue = (nextValue) => {
    onChange?.({
      target: {
        name,
        value: nextValue,
      },
    });
    onBlur?.({
      target: {
        name,
      },
    });
    setIsOpen(false);
  };

  const resolvedPlaceholder = placeholder || "Seleccionar";

  return (
    <FieldShell
      label={label}
      helper={helper}
      error={error}
      className={className}
      showLabel={showLabel}
    >
      <div ref={rootRef} className="relative z-30">
        <div className="relative">
          <button
            type="button"
            aria-haspopup="listbox"
            aria-expanded={isOpen}
            aria-controls={listboxId}
            aria-invalid={Boolean(error)}
            disabled={disabled}
            onClick={() => {
              if (!disabled) {
                setIsOpen((current) => !current);
              }
            }}
            onBlur={(event) => {
              if (!rootRef.current?.contains(event.relatedTarget)) {
                onBlur?.({
                  target: {
                    name,
                  },
                });
              }
            }}
            className={[
              brandSelectClass,
              "pr-10 text-left",
              disabled ? "" : "cursor-pointer",
            ]
              .filter(Boolean)
              .join(" ")}
          >
            <span className={selectedOption ? "text-brand-primary" : "text-slate-400"}>
              {selectedOption ? selectedOption.label : resolvedPlaceholder}
            </span>
          </button>
          <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-brand-primary/70">
            <MdKeyboardArrowDown
              aria-hidden="true"
              className={`h-5 w-5 shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`}
            />
          </div>

          {isOpen && !disabled && menuPosition && typeof document !== "undefined"
            ? createPortal(
                <div
                  ref={menuRef}
                  id={listboxId}
                  role="listbox"
                  aria-label={label || name}
                  className="fixed z-[9999] max-h-72 overflow-auto rounded-2xl border border-brand-secondary/20 bg-white/95 p-1 shadow-[0_16px_36px_rgba(20,50,135,0.14)] backdrop-blur"
                  style={{
                    top: `${menuPosition.top}px`,
                    left: `${menuPosition.left}px`,
                    width: `${menuPosition.width}px`,
                  }}
                >
                  {options.map((option) => {
                    const optionValue = toOptionValue(option.value);
                    const isSelected = optionValue === toOptionValue(value);

                    return (
                      <button
                        key={optionValue || option.label}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        disabled={option.disabled}
                        onMouseDown={(event) => {
                          event.preventDefault();
                        }}
                        onClick={() => commitValue(optionValue)}
                        className={[
                          "flex w-full items-center px-2 py-2 text-left text-sm transition-colors",
                          isSelected
                            ? "font-semibold text-brand-primary"
                            : disableHoverEffects
                              ? "text-slate-700"
                              : "text-slate-700 hover:text-brand-primary",
                          option.disabled ? "cursor-not-allowed opacity-50" : "",
                        ]
                          .filter(Boolean)
                          .join(" ")}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                </div>,
                document.body,
              )
            : null}
        </div>
      </div>
    </FieldShell>
  );
}
